import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircleMarker, GeoJSON, MapContainer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiAlertCircle, FiMaximize2, FiRefreshCw, FiX } from 'react-icons/fi';
import request from '../../../utils/request';
import usStatesGeo from '../../../data/us-states.geo.json';
import { fmtNumber } from '../derive';
import { EmptyState, ErrorState } from '../states';
import { US_BOUNDS, US_CENTER, stateName } from '../usStates';
import {
  COVERAGE_METRICS,
  EMPTY_FILL,
  NO_DRIVER_DEMAND_FILL,
  buildLegend,
  buildScale,
  bubbleRadius,
  metricValue,
  placeCities,
} from '../coverage';

// Driver coverage heat map: a state choropleth colored by the selected
// metric plus city bubbles sized by the same metric. Demand (properties,
// in-range orders) is shown alongside supply so under-served areas pop.

const ORDER_POINT_COLOR = '#6366f1';

function MapController({ focusState, fitToken }) {
  const map = useMap();
  const lastFocus = useRef(null);

  useEffect(() => {
    if (!focusState) {
      if (lastFocus.current) {
        map.fitBounds(US_BOUNDS, { padding: [8, 8] });
      }
      lastFocus.current = null;
      return;
    }
    const feature = usStatesGeo.features.find((f) => f.properties.code === focusState);
    if (!feature) return;
    const bounds = L.geoJSON(feature).getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 7 });
    lastFocus.current = focusState;
  }, [focusState, map]);

  useEffect(() => {
    if (fitToken > 0) map.fitBounds(US_BOUNDS, { padding: [8, 8] });
  }, [fitToken, map]);

  return null;
}

function SegmentedControl({ value, onChange, options, ariaLabel }) {
  return (
    <div className="flex items-center rounded-lg bg-zinc-100 p-0.5" role="tablist" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={active}
            title={opt.description || opt.label}
            onClick={() => onChange(opt.id)}
            className={`h-7 whitespace-nowrap rounded-md px-2 text-[11px] font-medium transition-colors duration-150 motion-reduce:transition-none ${
              active ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            {opt.short || opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Legend({ items, metricLabel, showDemandOnly }) {
  if (!items.length && !showDemandOnly) return null;
  return (
    <div className="pointer-events-none absolute bottom-3 left-3 z-[600] rounded-lg border border-zinc-200 bg-white/95 px-2.5 py-2 shadow-sm backdrop-blur">
      <p className="mb-1 text-[9.5px] font-semibold uppercase tracking-wider text-zinc-500">{metricLabel}</p>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.label} className="flex items-center gap-1.5 text-[10px] text-zinc-700">
            <span className="h-2.5 w-3.5 rounded-sm border border-black/5" style={{ background: item.color }} />
            {item.label}
          </li>
        ))}
        {showDemandOnly ? (
          <li className="flex items-center gap-1.5 text-[10px] text-zinc-700">
            <span className="h-2.5 w-3.5 rounded-sm border border-black/5" style={{ background: NO_DRIVER_DEMAND_FILL }} />
            Orders, no drivers
          </li>
        ) : null}
        <li className="flex items-center gap-1.5 text-[10px] text-zinc-500">
          <span className="h-2.5 w-3.5 rounded-sm border border-black/5" style={{ background: EMPTY_FILL }} />
          No data
        </li>
      </ul>
    </div>
  );
}

const tooltipHtml = (title, subtitle, row) => {
  const line = (label, value) =>
    `<div style="display:flex;justify-content:space-between;gap:12px"><span style="color:#71717a">${label}</span><b style="font-variant-numeric:tabular-nums">${value}</b></div>`;
  const ratio = row?.ordersPerActiveDriver;
  return `
    <div style="font-size:11px;min-width:170px">
      <div style="font-weight:600;color:#18181b">${title}</div>
      ${subtitle ? `<div style="color:#71717a;margin-bottom:4px">${subtitle}</div>` : '<div style="margin-bottom:4px"></div>'}
      ${line('Drivers', fmtNumber(row?.drivers || 0))}
      ${line('Active drivers', fmtNumber(row?.activeDrivers || 0))}
      ${line('Properties', fmtNumber(row?.properties || 0))}
      ${line('Orders in range', fmtNumber(row?.orders || 0))}
      ${line('Orders / active driver', ratio === null || ratio === undefined ? (row?.orders ? '∞' : '—') : ratio.toFixed(1))}
    </div>`;
};

export default function CanvasCoverageMap({
  coverage,
  loading = false,
  error = null,
  metric,
  onChangeMetric,
  layer,
  onChangeLayer,
  focusState,
  onFocusState,
  highlightId,
  onHighlight,
  onRefresh,
  refreshing = false,
}) {
  const [fitToken, setFitToken] = useState(0);
  const [orderPoints, setOrderPoints] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);

  const metricDef = COVERAGE_METRICS.find((m) => m.id === metric) || COVERAGE_METRICS[0];
  const states = useMemo(() => coverage?.states || [], [coverage]);
  const cities = useMemo(() => placeCities(coverage?.cities || []), [coverage]);
  const stateByCode = useMemo(() => new Map(states.map((row) => [row.state, row])), [states]);

  const stateScale = useMemo(
    () => buildScale(states, metricDef.id, { palette: metricDef.palette }),
    [states, metricDef]
  );
  const cityMax = useMemo(
    () => cities.reduce((max, city) => Math.max(max, metricValue(city, metricDef.id) || 0), 0),
    [cities, metricDef]
  );
  const legendItems = useMemo(() => buildLegend(stateScale, metricDef.id), [stateScale, metricDef]);
  const anyDemandWithoutDrivers = useMemo(
    () => states.some((row) => (row.orders || 0) > 0 && (row.activeDrivers || 0) === 0),
    [states]
  );

  // Orders layer reuses the lightweight /orders/map projection.
  useEffect(() => {
    if (layer !== 'orders' || orderPoints.length || orderLoading) return undefined;
    let cancel = false;
    (async () => {
      try {
        setOrderLoading(true);
        setOrderError(null);
        const { data } = await request.get('/orders/map', { params: { limit: 2500, status: 'completed' } });
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (cancel) return;
        setOrderPoints(
          list
            .map((d) => ({
              id: String(d._id || ''),
              lat: Number(d.storeLat),
              lng: Number(d.storeLng),
              label: d.storeLocation || d.uniqueId || 'Order',
            }))
            .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
        );
      } catch (e) {
        if (!cancel) setOrderError(e?.response?.data?.error || 'Failed to load order points');
      } finally {
        if (!cancel) setOrderLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [layer, orderPoints.length, orderLoading]);

  const styleFeature = useCallback(
    (feature) => {
      const code = feature?.properties?.code;
      const row = stateByCode.get(code);
      const value = metricValue(row, metricDef.id);
      let fill = stateScale.colorFor(value);
      if (
        metricDef.id === 'ordersPerActiveDriver' &&
        row &&
        (row.orders || 0) > 0 &&
        (row.activeDrivers || 0) === 0
      ) {
        fill = NO_DRIVER_DEMAND_FILL;
      }
      const isFocus = focusState && focusState === code;
      const isHighlight = highlightId && highlightId === code;
      const dim = focusState && !isFocus;
      return {
        fillColor: layer === 'orders' ? EMPTY_FILL : fill,
        fillOpacity: dim ? 0.25 : layer === 'orders' ? 0.5 : 0.85,
        color: isFocus || isHighlight ? '#18181b' : '#d4d4d8',
        weight: isFocus || isHighlight ? 2 : 0.8,
      };
    },
    [stateByCode, metricDef, stateScale, focusState, highlightId, layer]
  );

  const onEachFeature = useCallback(
    (feature, featureLayer) => {
      const code = feature?.properties?.code;
      const row = stateByCode.get(code);
      featureLayer.bindTooltip(tooltipHtml(stateName(code), code, row), { sticky: true, direction: 'top', opacity: 0.97 });
      featureLayer.on({
        click: () => onFocusState?.(focusState === code ? null : code),
        mouseover: () => onHighlight?.(code),
        mouseout: () => onHighlight?.(null),
      });
    },
    [stateByCode, onFocusState, onHighlight, focusState]
  );

  // react-leaflet's GeoJSON only reads style/onEachFeature on mount; key it
  // by everything that changes presentation so it re-renders cheaply.
  const geoKey = `${metricDef.id}|${layer}|${focusState || ''}|${highlightId || ''}|${coverage?.generatedAt || ''}`;

  const visibleCities = useMemo(() => {
    const list = focusState ? cities.filter((city) => city.state === focusState) : cities;
    return list
      .map((city) => ({ ...city, value: metricValue(city, metricDef.id) || 0 }))
      .filter((city) => city.value > 0 || ((city.orders || 0) > 0 && metricDef.id !== 'drivers'))
      .sort((a, b) => b.value - a.value);
  }, [cities, focusState, metricDef]);

  const totals = coverage?.totals;
  const unknown = coverage?.unknown;
  const hasData = states.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            ariaLabel="Map layer"
            value={layer}
            onChange={onChangeLayer}
            options={[
              { id: 'drivers', label: 'Drivers', description: 'Driver supply vs demand by state and city' },
              { id: 'orders', label: 'Orders', description: 'Completed order drop-off points' },
            ]}
          />
          {layer === 'drivers' ? (
            <SegmentedControl ariaLabel="Heat map metric" value={metricDef.id} onChange={onChangeMetric} options={COVERAGE_METRICS} />
          ) : null}
          {focusState ? (
            <button
              type="button"
              onClick={() => onFocusState?.(null)}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-900 bg-zinc-900 px-2 text-[11px] font-medium text-white"
              title="Clear state focus"
            >
              {stateName(focusState)}
              <FiX className="h-3 w-3" />
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-[10.5px] text-zinc-500">
          {totals ? (
            <span className="hidden laptop:inline">
              {fmtNumber(totals.drivers)} drivers · {fmtNumber(totals.activeDrivers)} active · {fmtNumber(totals.orders)} orders in range
            </span>
          ) : null}
          {unknown && (unknown.drivers || 0) > 0 ? (
            <span
              className="inline-flex items-center gap-1 rounded-md bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 ring-1 ring-orange-200"
              title="Drivers whose home address has no recognizable US state"
            >
              <FiAlertCircle className="h-3 w-3" />
              {fmtNumber(unknown.drivers)} unknown location
            </span>
          ) : null}
          {onRefresh ? (
            <button
              type="button"
              onClick={onRefresh}
              title={coverage?.cached ? 'Snapshot is cached for 24h. Click to recompute.' : 'Recompute coverage'}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
            >
              <FiRefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
              {coverage?.cached ? 'Cached' : 'Live'}
            </button>
          ) : null}
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-zinc-200">
        {error && !hasData ? (
          <ErrorState title="Could not load coverage" description={error} onRetry={onRefresh} />
        ) : !loading && !hasData ? (
          <EmptyState title="No coverage data" description="No drivers, properties or orders with a US address yet." />
        ) : (
          // Self-contained choropleth: no basemap tiles, so no third-party
          // tile keys or referer rules can break the heat map.
          <MapContainer
            style={{ height: '100%', width: '100%', background: '#f8fafc' }}
            center={US_CENTER}
            zoom={4}
            minZoom={3}
            maxZoom={10}
            scrollWheelZoom
            zoomControl
            attributionControl={false}
            preferCanvas
          >
            <GeoJSON key={geoKey} data={usStatesGeo} style={styleFeature} onEachFeature={onEachFeature} />

            {layer === 'drivers'
              ? visibleCities.map((city) => {
                  const radius = bubbleRadius(city.value, cityMax);
                  if (!radius) return null;
                  const highlighted = highlightId === city.id;
                  const uncovered = (city.orders || 0) > 0 && (city.activeDrivers || 0) === 0;
                  const color = uncovered && metricDef.id !== 'drivers' ? '#dc2626' : '#0f766e';
                  return (
                    <CircleMarker
                      key={city.id}
                      center={[city.lat, city.lng]}
                      radius={highlighted ? radius + 2 : radius}
                      pathOptions={{
                        color: highlighted ? '#18181b' : '#ffffff',
                        weight: highlighted ? 2 : 1,
                        fillColor: color,
                        fillOpacity: highlighted ? 0.85 : 0.6,
                      }}
                      eventHandlers={{
                        mouseover: () => onHighlight?.(city.id),
                        mouseout: () => onHighlight?.(null),
                        click: () => onFocusState?.(city.state),
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -radius]} opacity={0.97}>
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: tooltipHtml(
                              city.city,
                              `${city.state}${city.approximate ? ' · approx. location' : ''}`,
                              city
                            ),
                          }}
                        />
                      </Tooltip>
                    </CircleMarker>
                  );
                })
              : orderPoints.map((p) => (
                  <CircleMarker
                    key={p.id}
                    center={[p.lat, p.lng]}
                    radius={3}
                    pathOptions={{ color: ORDER_POINT_COLOR, weight: 0, fillOpacity: 0.45, fillColor: ORDER_POINT_COLOR }}
                  >
                    <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
                      <span className="text-[11px] text-zinc-700">{p.label}</span>
                    </Tooltip>
                  </CircleMarker>
                ))}

            <MapController focusState={focusState} fitToken={fitToken} />
          </MapContainer>
        )}

        {(loading && !hasData) || (layer === 'orders' && orderLoading) ? (
          <div className="pointer-events-none absolute inset-0 z-[650] flex items-center justify-center bg-white/60">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-200 border-t-emerald-600" />
          </div>
        ) : null}

        {hasData ? (
          <button
            type="button"
            onClick={() => {
              onFocusState?.(null);
              setFitToken((t) => t + 1);
            }}
            className="absolute right-3 top-3 z-[600] inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-zinc-700 shadow-sm backdrop-blur hover:bg-white"
          >
            <FiMaximize2 className="h-3 w-3" />
            Fit US
          </button>
        ) : null}

        {layer === 'drivers' && hasData ? (
          <Legend items={legendItems} metricLabel={metricDef.label} showDemandOnly={metricDef.id === 'ordersPerActiveDriver' && anyDemandWithoutDrivers} />
        ) : null}
        {layer === 'orders' && orderError ? (
          <div className="absolute bottom-3 left-3 z-[600] rounded-md bg-rose-50 px-2 py-1 text-[10.5px] text-rose-700 ring-1 ring-rose-200">{orderError}</div>
        ) : null}
      </div>
    </div>
  );
}
